import { FileType } from "../value-objects/FileType";
import { UploadStatus } from "../value-objects/UploadStatus";

export interface UploadTaskProps {
  readonly id: string;
  readonly name: string;
  readonly originalFileName: string;
  readonly type: FileType;
  readonly createdAt: string;
  progress: number;
  status: UploadStatus;
  error?: string;
  sizeMb?: number;
  durationSec?: number | null;
  placeholderRecordId?: string;
}

export interface UploadTaskSnapshot {
  id: string;
  name: string;
  originalFileName: string;
  type: FileType;
  createdAt: string;
  progress: number;
  status: UploadStatus;
  error?: string;
  sizeMb?: number;
  durationSec?: number | null;
  placeholderRecordId?: string;
}

export class UploadTask {
  private readonly props: UploadTaskProps;

  constructor(props: UploadTaskProps) {
    this.props = props;
  }

  get id(): string {
    return this.props.id;
  }

  get name(): string {
    return this.props.name;
  }

  get type(): FileType {
    return this.props.type;
  }

  get status(): UploadStatus {
    return this.props.status;
  }

  get progress(): number {
    return this.props.progress;
  }

  get originalFileName(): string {
    return this.props.originalFileName;
  }

  get createdAt(): string {
    return this.props.createdAt;
  }

  get error(): string | undefined {
    return this.props.error;
  }

  get sizeMb(): number | undefined {
    return this.props.sizeMb;
  }

  get durationSec(): number | null | undefined {
    return this.props.durationSec;
  }

  get placeholderRecordId(): string | undefined {
    return this.props.placeholderRecordId;
  }

  markPendingModal(): void {
    this.props.status = "pending_modal";
  }

  markUploading(): void {
    this.props.status = "uploading";
    this.props.progress = 0;
  }

  updateProgress(progress: number): void {
    this.props.progress = Math.max(0, Math.min(progress, 100));
  }

  markCompleted(options: {
    sizeMb?: number;
    durationSec?: number | null;
    placeholderRecordId?: string;
  }): void {
    this.props.status = "completed";
    this.props.progress = 100;
    this.props.sizeMb = options.sizeMb ?? this.props.sizeMb;
    this.props.durationSec =
      typeof options.durationSec === "undefined"
        ? this.props.durationSec
        : options.durationSec;
    this.props.placeholderRecordId =
      options.placeholderRecordId ?? this.props.placeholderRecordId;
  }

  markError(message: string): void {
    this.props.status = "error";
    this.props.error = message;
  }

  markCancelled(): void {
    this.props.status = "cancelled";
  }

  toSnapshot(): UploadTaskSnapshot {
    return {
      id: this.props.id,
      name: this.props.name,
      originalFileName: this.props.originalFileName,
      type: this.props.type,
      createdAt: this.props.createdAt,
      progress: this.props.progress,
      status: this.props.status,
      error: this.props.error,
      sizeMb: this.props.sizeMb,
      durationSec: this.props.durationSec,
      placeholderRecordId: this.props.placeholderRecordId,
    };
  }
}

